import { useState } from "react";
import ExerciseList from "./ExerciseList";
import warmupCooldown from "../data/warmupCooldown";

function expandZoneRefs(text, cfg) {
  if (!text || !cfg) return text || "";
  return text
    .replace(/{Z1}/g, `≤ ${cfg.z1_max} bpm`)
    .replace(/{Z2}/g, `${cfg.z1_max + 1}–${cfg.z2_max} bpm`)
    .replace(/{Z3}/g, `${cfg.z2_max + 1}–${cfg.z3_max} bpm`)
    .replace(/{Z4}/g, `${cfg.z3_max + 1}–${cfg.z4_max} bpm`)
    .replace(/{Z5}/g, `≥ ${cfg.z4_max + 1} bpm`)
    .replace(/{Z2_MAX}/g, String(cfg.z2_max));
}

const TABS = [
  { key: "Warmup", label: "🔥 Warmup", color: "#E67E22" },
  { key: "A", label: "Workout A", color: null },
  { key: "B", label: "Workout B", color: null },
  { key: "Cooldown", label: "❄️ Cooldown", color: "#2980B9" },
];

export default function PhaseView({ phase, zoneConfig }) {
  const [activeWeek, setActiveWeek] = useState(0);
  const [activeTab, setActiveTab] = useState("Warmup");
  const [expandedDays, setExpandedDays] = useState(new Set());
  const [showRaceDay, setShowRaceDay] = useState(false);
  const week = phase.weekData[activeWeek];
  const color = phase.color;
  const typeConfig = phase.typeConfig;

  function toggleDay(i) {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  return (
    <div
      style={{
        fontFamily: "'Georgia', serif",
        background: "var(--bg-page)",
        minHeight: "100vh",
        paddingBottom: 40,
      }}
    >
      {/* Header */}
      <div
        style={{
          background: color,
          padding: "28px 32px 24px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -40,
            right: -40,
            width: 180,
            height: 180,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
          }}
        />
        <div style={{ position: "relative" }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
            <span
              style={{
                background: "rgba(255,255,255,0.2)",
                color: "#fff",
                fontSize: 11,
                fontFamily: "monospace",
                letterSpacing: 2,
                padding: "3px 10px",
                borderRadius: 20,
              }}
            >
              QUARTER MARATHON PLAN
            </span>
            {phase.number === 4 && (
              <span
                style={{
                  background: "rgba(255,215,0,0.3)",
                  color: "#FFD700",
                  fontSize: 11,
                  fontFamily: "monospace",
                  letterSpacing: 2,
                  padding: "3px 10px",
                  borderRadius: 20,
                }}
              >
                FINAL PHASE
              </span>
            )}
          </div>
          <div style={{ color: "#fff", fontSize: 28, fontWeight: "bold" }}>
            Phase {phase.number} — {phase.title}
          </div>
          <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 14, marginTop: 4 }}>
            {phase.weeks} · {phase.dates}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
            {[phase.running, phase.hrTarget, phase.watchAlert].map((t, i) => (
              <div
                key={i}
                style={{
                  background: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  fontSize: 12,
                  padding: "5px 12px",
                  borderRadius: 6,
                }}
              >
                {expandZoneRefs(t, zoneConfig)}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ padding: "24px 20px 0", maxWidth: 720, margin: "0 auto" }}>
        {/* Taper warning — Phase 4 only */}
        {phase.number === 4 && (
          <div
            style={{
              background: "#FFFDE7",
              border: "1.5px solid #F9A825",
              borderRadius: 10,
              padding: "12px 16px",
              marginBottom: 16,
              display: "flex",
              gap: 8,
            }}
          >
            <span>⚠️</span>
            <div style={{ fontSize: 13, color: "#555" }}>
              <strong style={{ color: "#B7770D" }}>Taper reminder:</strong> Your fitness is already
              built. Resist the urge to add runs or extra sets — arriving fresh is the only goal
              now.
            </div>
          </div>
        )}

        {/* Legend */}
        <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
          {Object.entries(typeConfig).map(([k, v]) => (
            <div key={k} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: v.dot }} />
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{v.label}</span>
            </div>
          ))}
        </div>

        {/* Week selector */}
        <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
          {phase.weekData.map((w, i) => (
            <button
              key={i}
              onClick={() => {
                setActiveWeek(i);
                setExpandedDays(new Set());
                setShowRaceDay(false);
              }}
              style={{
                padding: "7px 14px",
                borderRadius: 8,
                border: "2px solid",
                borderColor: activeWeek === i ? color : "var(--border)",
                background: activeWeek === i ? color : "var(--bg-card)",
                color: activeWeek === i ? "#fff" : "var(--text-secondary)",
                fontFamily: "'Georgia', serif",
                fontSize: 13,
                cursor: "pointer",
                fontWeight: activeWeek === i ? "bold" : "normal",
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              {w.recovery && <span style={{ fontSize: 9 }}>↓</span>}
              {w.isRaceWeek && <span>🏁</span>}
              {w.label}
              {w.recovery && <span style={{ fontSize: 10, opacity: 0.8 }}>recovery</span>}
            </button>
          ))}
        </div>

        {/* Week focus */}
        <div
          style={{
            background: phase.light,
            border: `1.5px solid ${color}`,
            borderRadius: 10,
            padding: "12px 16px",
            marginBottom: 20,
            display: "flex",
            gap: 10,
          }}
        >
          <span style={{ fontSize: 18 }}>🎯</span>
          <div>
            <div
              style={{
                fontSize: 12,
                color: phase.accent,
                fontWeight: "bold",
                letterSpacing: 1,
                marginBottom: 2,
              }}
            >
              WEEK {week.week} FOCUS
            </div>
            <div style={{ fontSize: 14, color: "var(--text-secondary)" }}>{expandZoneRefs(week.focus, zoneConfig)}</div>
          </div>
        </div>

        {/* Daily schedule */}
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              fontSize: 12,
              color: "var(--text-subtle)",
              letterSpacing: 1,
              marginBottom: 10,
              fontFamily: "monospace",
            }}
          >
            WEEKLY SCHEDULE
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {week.days.map((d, i) => {
              const cfg = typeConfig[d.type] || typeConfig["rest"];
              const isExpanded = expandedDays.has(i);
              const hasDetail = d.type !== "rest";
              const isRace = d.type === "race";
              return (
                <div
                  key={i}
                  onClick={() => hasDetail && toggleDay(i)}
                  style={{
                    background: isRace ? "#FFFDE7" : cfg.bg,
                    border: `1.5px solid ${isExpanded ? color : isRace ? "#F9A825" : cfg.border}`,
                    borderRadius: 10,
                    overflow: "hidden",
                    cursor: hasDetail ? "pointer" : "default",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px" }}
                  >
                    <div
                      style={{
                        width: 36,
                        textAlign: "center",
                        fontSize: 13,
                        fontWeight: "bold",
                        color: "var(--text-secondary)",
                        fontFamily: "monospace",
                      }}
                    >
                      {d.day}
                    </div>
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: isRace ? "#F9A825" : cfg.dot,
                        flexShrink: 0,
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: isRace ? "bold" : "normal",
                          color: isRace ? "#B7770D" : "var(--text-primary)",
                        }}
                      >
                        {isRace ? "🏁 RACE DAY" : cfg.label}
                      </span>
                      {d.workout && (
                        <span style={{ fontSize: 12, color: "var(--text-muted)", marginLeft: 8 }}>
                          · Workout {d.workout}
                        </span>
                      )}
                      {d.run && (
                        <span
                          style={{
                            fontSize: 12,
                            color: isRace ? "#B7770D" : "var(--text-muted)",
                            marginLeft: 4,
                            fontWeight: isRace ? "bold" : "normal",
                          }}
                        >
                          · {expandZoneRefs(d.run, zoneConfig)}
                        </span>
                      )}
                      {!d.run && d.note && (
                        <span style={{ fontSize: 12, color: "var(--text-subtle)", marginLeft: 8 }}>{expandZoneRefs(d.note, zoneConfig)}</span>
                      )}
                    </div>
                    {hasDetail && (
                      <span style={{ fontSize: 11, color: "var(--text-faint)" }}>{isExpanded ? "▲" : "▼"}</span>
                    )}
                  </div>
                  {isExpanded && d.note && (
                    <div
                      style={{
                        padding: "8px 14px 10px 60px",
                        borderTop: `1px solid ${isRace ? "#F9A82544" : color + "22"}`,
                        background: "var(--bg-card)",
                      }}
                    >
                      <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>
                        💡 {expandZoneRefs(d.note, zoneConfig)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Race Day plan — Phase 4 Week 19 only */}
        {week.isRaceWeek && phase.raceDay && (
          <div style={{ marginBottom: 24 }}>
            <button
              onClick={() => setShowRaceDay(!showRaceDay)}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 10,
                border: "2px solid #F9A825",
                background: showRaceDay ? "#F9A825" : "#FFFDE7",
                color: showRaceDay ? "#fff" : "#B7770D",
                fontFamily: "'Georgia', serif",
                fontSize: 15,
                cursor: "pointer",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              🏁 {showRaceDay ? "Hide" : "Show"} Race Day Plan — June 27
            </button>
            {showRaceDay && (
              <div
                style={{
                  background: "var(--bg-card)",
                  border: "1.5px solid #F9A825",
                  borderRadius: 12,
                  overflow: "hidden",
                  marginTop: 8,
                }}
              >
                <div style={{ background: "#1A1A2E", padding: "12px 16px" }}>
                  <div style={{ color: "#FFD700", fontSize: 15, fontWeight: "bold" }}>
                    Futóest Quarter Marathon — Race Day Guide
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, marginTop: 2 }}>
                    10.55 km · Budapest · Evening race
                  </div>
                </div>
                {phase.raceDay.map((item, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      gap: 12,
                      padding: "12px 16px",
                      borderBottom: i < phase.raceDay.length - 1 ? `1px solid var(--border-subtle)` : "none",
                      background: i % 2 === 0 ? "var(--bg-alt)" : "var(--bg-card)",
                    }}
                  >
                    <div
                      style={{
                        width: 90,
                        fontSize: 12,
                        fontFamily: "monospace",
                        color: "#B7770D",
                        fontWeight: "bold",
                        flexShrink: 0,
                        paddingTop: 2,
                      }}
                    >
                      {item.time}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: "bold", color: "var(--text-primary)" }}>
                        {item.action}
                      </div>
                      <div
                        style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2, fontStyle: "italic" }}
                      >
                        {item.detail}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Session Guide — hidden on race week */}
        {!week.isRaceWeek && (
          <div style={{ marginBottom: 12 }}>
            <div
              style={{
                fontSize: 12,
                color: "var(--text-subtle)",
                letterSpacing: 1,
                marginBottom: 10,
                fontFamily: "monospace",
              }}
            >
              SESSION GUIDE
            </div>
            <div
              style={{
                background: "var(--bg-subtle)",
                borderRadius: 10,
                padding: 4,
                display: "flex",
                gap: 2,
                marginBottom: 14,
              }}
            >
              {TABS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    flex: 1,
                    padding: "8px 4px",
                    borderRadius: 7,
                    border: "none",
                    background: activeTab === tab.key ? "var(--bg-card)" : "transparent",
                    color: activeTab === tab.key ? tab.color || color : "var(--text-subtle)",
                    fontFamily: "'Georgia', serif",
                    fontSize: 12,
                    cursor: "pointer",
                    fontWeight: activeTab === tab.key ? "bold" : "normal",
                    boxShadow: activeTab === tab.key ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                    whiteSpace: "nowrap",
                    transition: "all 0.15s",
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 4,
                marginBottom: 14,
                fontSize: 11,
                color: "var(--text-faint)",
                flexWrap: "wrap",
              }}
            >
              {["🔥 Warmup", "→", "KB Workout", "→", "Run", "→", "❄️ Cooldown"].map((s, i) => (
                <span key={i} style={{ color: s === "→" ? "var(--border)" : "var(--text-subtle)" }}>
                  {s}
                </span>
              ))}
            </div>
            {activeTab === "Warmup" && (
              <ExerciseList
                exercises={warmupCooldown.warmup.exercises}
                duration={warmupCooldown.warmup.duration}
                note={warmupCooldown.warmup.note}
                hColor="#E67E22"
                phaseColor={color}
                showWakeLock
                showCountdown
              />
            )}
            {(activeTab === "A" || activeTab === "B") && (
              <ExerciseList
                exercises={phase.workouts[activeTab].exercises}
                duration={phase.workouts[activeTab].duration}
                phaseColor={color}
              />
            )}
            {activeTab === "Cooldown" && (
              <ExerciseList
                exercises={warmupCooldown.cooldown.exercises}
                duration={warmupCooldown.cooldown.duration}
                note={warmupCooldown.cooldown.note}
                hColor="#2980B9"
                phaseColor={color}
                showWakeLock
                showCountdown
              />
            )}
          </div>
        )}

        <div
          style={{
            textAlign: "center",
            padding: "20px 0 0",
            borderTop: `1px solid var(--border)`,
            marginTop: 20,
          }}
        >
          <div style={{ fontSize: 12, color: "var(--text-faint)", fontStyle: "italic" }}>
            Phase {phase.number} of 4{phase.number < 4 && ` · Next: Phase ${phase.number + 1}`}
            {phase.number === 4 && " · After June 27: Half Marathon Phase begins"}
          </div>
        </div>
      </div>
    </div>
  );
}
